import { computed } from 'vue';

/**
 * useAbonoCartTotal
 *
 * Computes the total cart value for the abonos checkout (`datos_guardados`)
 * with full fidelity to the totals shown in `ResumenCompraComponent.vue`:
 * voucher discounts (percent / fixed), wallet discount (`descuento_monedero`),
 * shipping (`envio_domicilio`), plazos mode, and extra purchased parcelas
 * (`datos_parcelas_compra`).
 *
 * The composable accepts getter functions so it can be wired from Options API
 * components where cart data lives in `data()` and is mutated asynchronously
 * during the wizard.
 *
 * Returned totals:
 *  - totalSinGd        sum of `descuento.precio` per item, with voucher / monedero / envio.
 *  - totalConGd        same, using `descuento.precio_total` (includes per-item `price_management`).
 *  - plazosSinGd       plazos mode: sum of `plazos_pendientes[0].importe` + envio.
 *  - plazosConGd       plazos mode: sum of `plazos_pendientes[0].importe + gastos` (when
 *                       `gastos_internos == 0`) + envio.
 *  - conParcelasSinGd  totalSinGd + sum of parcela `precio` (no gd).
 *  - conParcelasConGd  totalConGd + sum of parcela `precio + price_management` (when externo).
 *  - gd                sum of `descuento.price_management` for items where it's externo
 *                       (i.e. `price_management_interno` is falsy).
 *  - parcelasGd        sum of parcela `price_management` for externo parcelas.
 *
 * @param {Object} deps
 * @param {Function} [deps.getDatosGuardados]      Returns the `datos_guardados` array (or null).
 * @param {Function} [deps.getEnvioDomicilio]      Returns whether envio a domicilio is active.
 * @param {Function} [deps.getListaAbonados]      Returns the `lista_abonados` object (for envio price).
 * @param {Function} [deps.getDatosParcelasCompra] Returns the extra purchased parcelas array (or null).
 */
export function useAbonoCartTotal(deps = {}) {
    const {
        getDatosGuardados = () => null,
        getEnvioDomicilio = () => false,
        getListaAbonados = () => null,
        getDatosParcelasCompra = () => null,
    } = deps;

    const envioPrice = computed(() => {
        const la = getListaAbonados();
        return parseFloat(la?.precio_envio_a_domicilio || 0);
    });

    const envioActive = computed(() => {
        const la = getListaAbonados();
        const domicilio = getEnvioDomicilio();
        return Boolean(la?.permitir_envio_a_domicilio) && Boolean(domicilio);
    });

    const itemsSinGd = computed(() => {
        const dg = getDatosGuardados() || [];
        let total = 0;
        for (let i = 0; i < dg.length; i++) {
            const item = dg[i];
            if (!item?.descuento) continue;
            const base = parseFloat(item.descuento.precio || 0);
            if (item.voucher) {
                if (parseFloat(item.voucher.discount_percent) > 0) {
                    total += base - (base * (parseFloat(item.voucher.discount_percent) / 100));
                } else {
                    total += base - parseFloat(item.voucher.discount_fixed || 0);
                }
            } else {
                total += base;
            }
            if (item.descuento_monedero) {
                total -= parseFloat(item.descuento_monedero);
            }
        }
        return total;
    });

    const itemsConGd = computed(() => {
        const dg = getDatosGuardados() || [];
        let total = 0;
        for (let i = 0; i < dg.length; i++) {
            const item = dg[i];
            if (!item?.descuento) continue;
            const totalConGd = parseFloat(item.descuento.precio_total || 0);
            const baseSinGd = parseFloat(item.descuento.precio || 0);
            if (item.voucher) {
                if (parseFloat(item.voucher.discount_percent) > 0) {
                    total += totalConGd - (baseSinGd * (parseFloat(item.voucher.discount_percent) / 100));
                } else {
                    total += totalConGd - parseFloat(item.voucher.discount_fixed || 0);
                }
            } else {
                total += totalConGd;
            }
            if (item.descuento_monedero) {
                total -= parseFloat(item.descuento_monedero);
            }
        }
        return total;
    });

    const gd = computed(() => {
        const dg = getDatosGuardados() || [];
        let total = 0;
        for (let i = 0; i < dg.length; i++) {
            const item = dg[i];
            if (!item?.descuento) continue;
            if (!item.descuento.price_management_interno) {
                total += parseFloat(item.descuento.price_management || 0);
            }
        }
        return total;
    });

    const totalSinGd = computed(() => {
        let total = itemsSinGd.value;
        if (envioActive.value) total += envioPrice.value;
        return total;
    });

    const totalConGd = computed(() => {
        let total = itemsConGd.value;
        if (envioActive.value) total += envioPrice.value;
        return total;
    });

    const plazosSinGd = computed(() => {
        const dg = getDatosGuardados() || [];
        let total = 0;
        for (let i = 0; i < dg.length; i++) {
            const item = dg[i];
            const plazo = item?.plazos_pendientes?.[0];
            if (plazo) {
                total += parseFloat(plazo.importe || 0);
            }
        }
        if (envioActive.value) total += envioPrice.value;
        return total;
    });

    const plazosConGd = computed(() => {
        const dg = getDatosGuardados() || [];
        let total = 0;
        for (let i = 0; i < dg.length; i++) {
            const item = dg[i];
            const plazo = item?.plazos_pendientes?.[0];
            if (plazo) {
                if (parseFloat(plazo.gastos_internos || 0) === 0) {
                    total += parseFloat(plazo.importe || 0) + parseFloat(plazo.gastos || 0);
                } else {
                    total += parseFloat(plazo.importe || 0);
                }
            }
        }
        if (envioActive.value) total += envioPrice.value;
        return total;
    });

    const parcelasSinGd = computed(() => {
        const parcelas = getDatosParcelasCompra() || [];
        return parcelas.reduce((sum, parcela) => {
            return sum + parseFloat(parcela?.precioSeleccionado?.precio || 0);
        }, 0);
    });

    const parcelasConGd = computed(() => {
        const parcelas = getDatosParcelasCompra() || [];
        return parcelas.reduce((sum, parcela) => {
            const ps = parcela?.precioSeleccionado;
            if (!ps) return sum;
            const precio = parseFloat(ps.precio || 0);
            if (ps.price_management_interno) {
                return sum + precio;
            }
            return sum + precio + parseFloat(ps.price_management || 0);
        }, 0);
    });

    const parcelasGd = computed(() => {
        const parcelas = getDatosParcelasCompra() || [];
        return parcelas.reduce((sum, parcela) => {
            const ps = parcela?.precioSeleccionado;
            if (!ps || ps.price_management_interno) return sum;
            return sum + parseFloat(ps.price_management || 0);
        }, 0);
    });

    const conParcelasSinGd = computed(() => totalSinGd.value + parcelasSinGd.value);
    const conParcelasConGd = computed(() => totalConGd.value + parcelasConGd.value);

    const hasPlazos = computed(() => {
        const dg = getDatosGuardados() || [];
        return Boolean(dg[0]?.plazos);
    });

    const hasParcelas = computed(() => {
        const parcelas = getDatosParcelasCompra() || [];
        return parcelas.length > 0;
    });

    /**
     * Active cart total WITHOUT gastos de gestion — used for IC eligibility check.
     * Matches the displayed total in the resumen (base price) excluding per-item gd.
     */
    const cartBase = computed(() => {
        if (hasPlazos.value) return plazosSinGd.value;
        if (hasParcelas.value) return conParcelasSinGd.value;
        return totalSinGd.value;
    });

    /**
     * Active cart total WITH gastos de gestion — used when matching the charge
     * the user will see (sum of precio_total + envio, with voucher / monedero applied).
     */
    const cartTotal = computed(() => {
        if (hasPlazos.value) return plazosConGd.value;
        if (hasParcelas.value) return conParcelasConGd.value;
        return totalConGd.value;
    });

    return {
        envioPrice,
        envioActive,
        itemsSinGd,
        itemsConGd,
        gd,
        parcelasGd,
        totalSinGd,
        totalConGd,
        plazosSinGd,
        plazosConGd,
        parcelasSinGd,
        parcelasConGd,
        conParcelasSinGd,
        conParcelasConGd,
        hasPlazos,
        hasParcelas,
        cartBase,
        cartTotal,
    };
}
